import type { LatLng } from '../game/geo';
import type { GameMap, GameMapId } from '../game/maps';

export type ProviderId = 'google' | 'mapillary' | 'panoramax';

export interface PanoramaAttribution {
  /** Name of the imagery source, always shown. */
  sourceName: string;
  /** Photographer / contributor, revealed after the round. */
  author?: string;
  /** Licence of the picture (SPDX id or text). */
  license?: string;
  /** Link to the picture on the provider's website. */
  sourceUrl?: string;
  /** Capture date (ISO 8601). */
  capturedAt?: string;
}

export interface Panorama {
  provider: ProviderId;
  /** Identifier of the picture at the provider. */
  id: string;
  /** Real GPS position of the picture. This is the answer of the round. */
  position: LatLng;
  /** Heading (degrees clockwise from north) of the centre of the panorama. */
  heading: number;
  /** Equirectangular image URLs for the sphere viewer, best first (empty for Google). */
  imageUrls: string[];
  attribution: PanoramaAttribution;
  /** Provider specific data (navigation links…). */
  meta?: unknown;
}

export interface NeighborLink {
  id: string;
  /** Where the neighbour picture was taken. */
  position: LatLng;
  load: (signal?: AbortSignal) => Promise<Panorama>;
}

export interface FindOptions {
  signal: AbortSignal;
  /** Already played positions: new locations must not be too close to them. */
  exclude: LatLng[];
  rng?: () => number;
}

export interface PanoramaProvider {
  id: ProviderId;
  name: string;
  description: string;
  /** Which component displays the panorama. */
  viewer: 'sphere' | 'google';
  /** Which component displays the guess map (Google imagery must be used with a Google map). */
  mapKind: 'leaflet' | 'google';
  /** Name of the environment variable needed to enable this provider, if any. */
  envVar?: string;
  isConfigured(): boolean;
  supportedMaps: GameMapId[];
  findRandom(map: GameMap, options: FindOptions): Promise<Panorama>;
  getNeighbors?(panorama: Panorama, signal?: AbortSignal): Promise<NeighborLink[]>;
  /** Closest 360° picture to `target` within `radiusKm` (other than `excludeId`), for click-to-go. */
  findNearby?(target: LatLng, options: { radiusKm: number; excludeId: string; signal?: AbortSignal }): Promise<Panorama | null>;
}

/** The provider cannot be used at all right now (bad key, service down, network blocked…). */
export class ProviderUnavailableError extends Error {
  constructor(
    public readonly providerId: ProviderId,
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ProviderUnavailableError';
  }
}

/** The provider works but no suitable panorama was found after several attempts. */
export class NoPanoramaFoundError extends Error {
  constructor(
    public readonly providerId: ProviderId,
    message: string,
  ) {
    super(message);
    this.name = 'NoPanoramaFoundError';
  }
}
